<template>
  <div class="basic-input-wrapper">
    <CurrencyInput
      ref="currencyInput"
      v-if="type === 'currency'"
      v-model="internalValue"
      color="unset"
      :id="inputId"
      :disableAutoFocus="disableAutoFocus"
      @blur="handleBlur"
      :options="currencyInputOptions"
      :style="currencyInputStyle"
      :class="['basic-input', {isErrorTooltip: errorTooltipText}]"
    />
    <Input
      v-else
      ref="basicInput"
      :id="inputId"
      :type="type == 'number' ? 'number' : 'text'"
      :disableAutoFocus="disableAutoFocus"
      :min="min"
      :max="max"
      :maxLength="maxLength"
      :minLength="minLength"
      :isOnlyNumber="isOnlyNumber"
      :value="internalValue"
      @blur="handleBlur"
      :style="inputStyle"
      :class="['basic-input', {isErrorTooltip: errorTooltipText}]"
    />
    <div v-if="errorTooltipText && (!isValidInternal)" class="basic-input-icon">
      <TooltipPlugin :tooltipText="errorTooltipText" >
        <template #tooltip-btn>
          <img src="@/assets/images/icons/alert-circle.svg" />
        </template>
      </TooltipPlugin>
    </div>
    <div v-if="isLoadingState" class="basic-input-icon">
      <img src="@/assets/images/icons/loading.svg" class="loading" />
    </div>
  </div>
</template>

<script>
import { Input, Button, TooltipPlugin } from '@n11/so-ui-components';
import CurrencyInput from './CurrencyInput'

export default {
  name: 'BasicInput',
  components: { Input, Button, CurrencyInput, TooltipPlugin },
  props: {
    inputId: {
      type: String,
      default: "",
    },
    value: {
      type: [String, Number, null],
      required: true,
    },
    blur: {
      type: Function,
      required: true
    },
    isValid: {
      type: Boolean,
      required: true
    },
    isLoading: {
      type: Boolean,
      required: false
    },
    isPrice: {
      type: Boolean,
      required: false,
      default: false
    },
    format: {
      type: Function,
      required: false,
      default: value => value
    },
    isDisabled: {
      type: Boolean,
      default: false,
      required: false,
    },
    type: {
      type: String,
      required: false
    },
    min: {
      type: Number,
      required: false
    },
    max: {
      type: Number,
      required: false
    },
    minLength: {
      type: Number,
      required: false
    },
    maxLength: {
      type: Number,
      required: false
    },
    isOnlyNumber: {
      type: Boolean,
      required: false
    },
    disableAutoFocus: {
      type: Boolean,
      required: false
    },
    errorTooltipText: {
      type: String,
      required: false,
      default: "",
    },
    currencyDisplay: {
      type: String,
      default: 'hidden', 
    },
    currencyCode: {
      type: String,
      required: false,
    },
  },
  data() {
    return {
      internalValue: this.value,
      isValidInternal: this.isValid,
      previousValue: this.value,
      isLoadingState: this.isLoading,
      timeout: null,
    };
  },
  computed: {
    currencyInputOptions() {
      return { 
        locale: 'tr-TR',
        currency: this.currencyCode,
        currencyDisplay: this.currencyDisplay,
        hideGroupingSeparatorOnFocus: false,
        hideNegligibleDecimalDigitsOnFocus: false,
        autoDecimalDigits: false,
        useGrouping: true,
        accountingSign: false,
        valueRange: { min: 0 },
      }
    },
    currencyInputStyle() {
      return {
        borderColor: (!this.isValidInternal) ? '#FF3939' : '',
        border: (!this.isValidInternal) ? '1px solid #FF3939' : '',
        outline: (!this.isValidInternal) ? '3px solid #FFD7D7' : '',
      }
    },
    inputStyle() {
      return {
        borderColor: (!this.isValidInternal) ? '#FF3939' : '',
        outline: (!this.isValidInternal) ? '3px solid #FFD7D7' : ''
      }
    }
  },
  watch: {
    isValid(newValidity) {
      this.isValidInternal = newValidity;
    },
    isLoading(isLoading) {
      this.isLoadingState = isLoading;
    },
    value: function (newValue) {
      if (this.isPrice) {
        this.$refs.currencyInput?.setValue(newValue);
      } else {
        this.internalValue = newValue;
      }
    }
  },
  mounted() {
    if (this.isPrice) {
      this.$refs?.currencyInput?.$el?.querySelector('input')?.addEventListener('focus', this.handleScrollIntoView);
    } else {
      this.$refs?.basicInput?.$el?.querySelector('input')?.addEventListener('focus', this.handleScrollIntoView);
    }
  },
  unmounted() {
    if (this.isPrice) {
      this.$refs?.currencyInput?.$el?.querySelector('input')?.removeEventListener('focus', this.handleScrollIntoView);
    } else {
      this.$refs?.basicInput?.$el?.querySelector('input')?.removeEventListener('focus', this.handleScrollIntoView);
    }
  },
  methods: {
    handleScrollIntoView(event) {
      event.target.scrollIntoView({ behavior: 'smooth', inline: 'nearest' })
    },
    handleBlur(e) {
      if (!this.isPrice) {
        this.internalValue = e.target.value
      }

      this.$emit('input', this.internalValue);

      if (this.blur) this.blur(this.internalValue);
    },
  },
};
</script>

<style lang="scss" scoped>
@import '@/assets/styles/variables.scss';
.basic-input-wrapper {
  display: inline-flex;
  padding: 10px 12px;
  flex-direction: column;
  align-items: flex-start;
  width: 100%;
  position: relative;

  label {
    font-size: 12px;
    font-weight: 700;
    line-height: 16px;
    color: $colorN80;
  }
  .disabled-button {
    background-color: grey;
    cursor: not-allowed;
  }

  :deep(.basic-input) {
    scroll-margin: 80px;
  }

  :deep(.basic-input.isErrorTooltip) {
    padding-right: 28px !important;
  }

  .basic-input-icon {
    position: absolute;
    z-index: 1;
    right: 18px;
    top: 21px;
    cursor: pointer;

    img {
      width: 18px;
      height: 18px;
    }

    .loading {
      animation: rotate360 2s linear infinite;
      transform-origin: center center;
    }

    @keyframes rotate360 {
      from {
        transform: rotate(0deg);
      }
      to {
        transform: rotate(360deg);
      }
    }
  }

}
</style>
